import type {
	PortalCheckoutPaymentMethodDto,
	SupportedPaymentMethodType,
} from '@supply-mover-2-0/shared';
import { CardCategory, normalizeCardCategory } from '@supply-mover-2-0/shared';
import { PaymentMethodTypeEnum } from '../types/payment.types';
import { formatCurrency } from './formatters';
import {
	cardTypeLabel,
	isCardPaymentType,
	paymentTypeLabel,
} from './payment-type-label';

/**
 * PD-16381 — how a saved payment method is named and priced wherever a
 * customer picks one to pay with.
 *
 * The storefront's payment screens each grew their own copy of these rules:
 * the multi-account Make Payment view priced every option in its label, while
 * Pay Invoice and single-account Make Payment asked for a payment TYPE first
 * and priced the type. Once those two surfaces became one flat list of saved
 * methods, every surface needed the same answer to "what is this method called
 * and what does it cost", so the answer lives here once and cannot drift
 * between them.
 */

type TranslateFn = (
	key: string,
	params?: Record<string, string | number | null | undefined>,
) => string;

/** The saved-method fields a picker option is built from. */
export type PaymentMethodOptionSourceModel = Pick<
	PortalCheckoutPaymentMethodDto,
	| 'type'
	| 'subtype'
	| 'lastFour'
	| 'name'
	| 'cardCategoryLabel'
	| 'cardCategory'
> &
	// PD-17009 — optional so the callers that build an option from a bare
	// method (no owner in hand) keep compiling; such a method reads as the
	// account's own.
	Partial<Pick<PortalCheckoutPaymentMethodDto, 'relation' | 'ownerClientName'>>;

/**
 * PD-17009 — the saved methods a picker may choose on the customer's behalf:
 * every method except the ones another account shares through the ownership
 * group. A shared method is always offered and never preselected, so no screen
 * opens with someone else's card or bank account already chosen. Every
 * storefront picker passes its list through this before picking a default; the
 * customer can still choose a shared method themselves.
 */
export function paymentMethodsEligibleForDefault<
	T extends Partial<Pick<PortalCheckoutPaymentMethodDto, 'relation'>>,
>(methods: Array<T>): Array<T> {
	return methods.filter((method) => method.relation !== 'ownership');
}

/**
 * PD-17009 — "Shared by {{account}}" for a method another account shares with
 * the paying account through their ownership group; empty for any other
 * method. Every storefront picker builds its options through
 * `formatPaymentMethodOptionLabel`, which adds this, so a shared method is
 * labeled the same way wherever it can be chosen.
 */
export function formatPaymentMethodSharedByLabel(
	method: Pick<PaymentMethodOptionSourceModel, 'relation' | 'ownerClientName'>,
	t: TranslateFn,
): string {
	if (method.relation !== 'ownership') {
		return '';
	}

	return method.ownerClientName != null && method.ownerClientName !== ''
		? t('Shared by {{account}}', { account: t(method.ownerClientName) })
		: t('Shared by another account');
}

/** The saved-method fields that decide whether a fee applies to it. */
export type PaymentMethodFeeSubjectModel = Pick<
	PortalCheckoutPaymentMethodDto,
	'type'
> &
	Partial<Pick<PortalCheckoutPaymentMethodDto, 'isSurchargeable'>>;

/** The payment-type row fields the fee rules read. */
export type PaymentTypeFeeRateModel = Pick<
	SupportedPaymentMethodType,
	'paymentMethodType' | 'processingFeeType' | 'processingFee'
>;

/**
 * The on-screen name of a saved method: its network (or type), the last four
 * digits, the name the customer gave it, and — for a card — its funding type.
 *
 * The funding type is included because a card quoted with no fee needs to say
 * why (PD-15154): "Debit card" explains "No fee", where silence reads as an
 * omission.
 *
 * PD-16381 — a card with no network name is named by its funding ("Debit
 * Card", or just "Card" when unclassified), never by the stored 'Credit Card'
 * type. When that name already states the funding, the funding suffix is
 * dropped rather than said twice. A picker shows the same parts on two lines
 * instead (`formatPaymentMethodOptionLabel`).
 */
export function formatPaymentMethodLabel(
	method: PaymentMethodOptionSourceModel,
	t: TranslateFn,
): string {
	const funding = formatPaymentMethodFundingLabel(method, t);

	return [
		formatPaymentMethodPrimaryLabel(method, t),
		funding.length > 0 ? `— ${funding}` : '',
	]
		.filter((part) => part.length > 0)
		.join(' ');
}

/**
 * PD-16381 — what a saved method IS, the first line of a picker option: its
 * network (or type), the last four digits and the name the customer gave it.
 * "Visa ****9497 (Office card)", "Debit Card ****0008", "Checking ****6789".
 */
export function formatPaymentMethodPrimaryLabel(
	method: PaymentMethodOptionSourceModel,
	t: TranslateFn,
): string {
	return [
		t(method.subtype ?? paymentTypeLabel(method.type, method.cardCategory)),
		method.lastFour != null ? `****${method.lastFour}` : '',
		method.name != null ? `(${t(method.name)})` : '',
	]
		.filter((part) => part.length > 0)
		.join(' ');
}

/**
 * PD-16381 — a card's funding type ("Credit card", "Debit card", "Prepaid
 * card"), translated. Empty for anything that is not a card, for a card whose
 * primary label already names its funding ("Debit Card ****0008"), which
 * would otherwise say it twice, and for a card whose funding nobody has
 * established — never classified, or classified as unknown. Such a card is
 * only ever called "Card", and the first line already names it, so there is
 * nothing true to add ("Unclassified" was jargon to the customer reading it).
 */
export function formatPaymentMethodFundingLabel(
	method: PaymentMethodOptionSourceModel,
	t: TranslateFn,
): string {
	const category = normalizeCardCategory(method.cardCategory);
	if (category == null || category === CardCategory.UNKNOWN) {
		return '';
	}

	const fundingInName =
		method.subtype == null &&
		isCardPaymentType(method.type) &&
		paymentTypeLabel(method.type, method.cardCategory) !== cardTypeLabel(null);

	return method.cardCategoryLabel != null && !fundingInName
		? t(method.cardCategoryLabel)
		: '';
}

/** PD-16381 — a saved method's picker option, split across two lines. */
export interface PaymentMethodOptionLabelModel {
	/** What the method is (`formatPaymentMethodPrimaryLabel`). */
	primary: string;
	/**
	 * The smaller second line: the funding type, the fee and the owning
	 * account, whichever apply, joined with " · ". Empty when none do.
	 */
	secondary: string;
	/**
	 * The fee on its own ("3% fee", "No fee"), empty when none is quoted. It is
	 * the one detail still worth seeing once the method is chosen, so a closed
	 * picker keeps it beside the primary line.
	 */
	fee: string;
}

export interface PaymentMethodOptionLabelOptionsModel {
	/**
	 * The payment types that price the method. Omitted or null where the picker
	 * quotes no fee: checkout prices the payment type on its own row, and an
	 * invoice whose balance already carries a fee is charged as-is (PD-15688).
	 */
	feeTypes?: Array<PaymentTypeFeeRateModel> | null;
	/**
	 * The owning account's name, passed only where the picker spans accounts:
	 * two cards can end in the same four digits.
	 */
	accountName?: string | null;
}

/**
 * PD-16381 — a saved method's picker option as two lines: what the method is
 * on the first and, smaller, what choosing it means on the second — its
 * funding type, its fee and whose it is. On one line those details ran
 * together ("MasterCard ****4444 (Office) — Credit card — 3% fee — Bonneville
 * Meats") and the control cut them off well before the fee.
 */
export function formatPaymentMethodOptionLabel(
	method: PaymentMethodOptionSourceModel & PaymentMethodFeeSubjectModel,
	t: TranslateFn,
	options: PaymentMethodOptionLabelOptionsModel = {},
): PaymentMethodOptionLabelModel {
	const fee =
		options.feeTypes != null
			? formatPaymentMethodFeeLabel(options.feeTypes, method, t)
			: '';
	const account =
		options.accountName != null && options.accountName !== ''
			? t(options.accountName)
			: '';
	// PD-17009 — a shared method always says whose it is, on every picker,
	// whether or not the picker spans accounts. It replaces the bare account
	// name rather than joining it, which would name the owner twice.
	const sharedBy = formatPaymentMethodSharedByLabel(method, t);

	return {
		primary: formatPaymentMethodPrimaryLabel(method, t),
		secondary: [
			formatPaymentMethodFundingLabel(method, t),
			fee,
			sharedBy !== '' ? sharedBy : account,
		]
			.filter((part) => part.length > 0)
			.join(' · '),
		fee,
	};
}

/**
 * The payment-type row that prices a saved method: the one whose type the
 * method is. Null when the list on offer has no such type, which also means
 * the method cannot be used on that surface.
 */
export function findPaymentTypeForMethod<
	T extends Pick<SupportedPaymentMethodType, 'paymentMethodType'>,
>(
	types: Array<T>,
	method: Pick<PortalCheckoutPaymentMethodDto, 'type'>,
): T | null {
	return types.find((type) => type.paymentMethodType === method.type) ?? null;
}

/**
 * The saved methods a customer can actually pay with on a surface: those whose
 * type the surface offers. A saved bank account is not a choice on a surface
 * that does not take ACH, however long it has been on file.
 */
export function filterUsablePaymentMethods<
	M extends Pick<PortalCheckoutPaymentMethodDto, 'type'>,
>(
	methods: Array<M>,
	types: Array<Pick<SupportedPaymentMethodType, 'paymentMethodType'>>,
): Array<M> {
	return methods.filter(
		(method) => findPaymentTypeForMethod(types, method) != null,
	);
}

/**
 * The rate charged on a saved method, or null when it carries no fee.
 *
 * The rate is the payment type's, already resolved server-side (client
 * override and fee-state gate applied), so a zero here genuinely means
 * nothing is added. On top of that, only a card that is positively a CREDIT
 * card may be surcharged (PD-15173): Regulation II prohibits surcharging
 * debit and the networks extend that to prepaid, so a card the server marks
 * non-surchargeable pays nothing even when its type has a rate.
 */
export function resolvePaymentMethodFeeRate<T extends PaymentTypeFeeRateModel>(
	types: Array<T>,
	method: PaymentMethodFeeSubjectModel,
): T | null {
	const type = findPaymentTypeForMethod(types, method);

	if (type == null || type.processingFee <= 0) {
		return null;
	}

	return method.isSurchargeable === false ? null : type;
}

/**
 * The fee a rate adds to an amount. A percentage is FLOORED to the cent,
 * matching what the server charges (PD-15174): a rate sitting on the
 * surcharge cap must never round above it. A flat fee is charged as-is.
 */
export function calculatePaymentMethodFee(
	amount: number,
	rate: Pick<SupportedPaymentMethodType, 'processingFeeType' | 'processingFee'>,
): number {
	return rate.processingFeeType === '%'
		? Math.floor(amount * rate.processingFee) / 100
		: rate.processingFee;
}

/**
 * The fee a saved method is quoted with (PD-15011): "3% fee", "$1.50 fee", or
 * "No fee". Empty when the list does not offer the method's type, since there
 * is nothing honest to say about it.
 *
 * Fee-free options are labelled explicitly rather than left blank: silence
 * reads as "unknown", not "free", and the cheaper option should get visible
 * credit for being cheaper.
 */
export function formatPaymentMethodFeeLabel(
	types: Array<PaymentTypeFeeRateModel>,
	method: PaymentMethodFeeSubjectModel,
	t: TranslateFn,
): string {
	if (findPaymentTypeForMethod(types, method) == null) {
		return '';
	}

	const rate = resolvePaymentMethodFeeRate(types, method);

	if (rate == null) {
		return t('No fee');
	}

	return rate.processingFeeType === '%'
		? `${rate.processingFee}% ${t('fee')}`
		: `${formatCurrency(rate.processingFee)} ${t('fee')}`;
}

/**
 * The fee disclosure appended to a one-line option label: " — 3% fee",
 * " — $1.50 fee", " — No fee", or empty (see `formatPaymentMethodFeeLabel`).
 */
export function formatPaymentMethodFeeSuffix(
	types: Array<PaymentTypeFeeRateModel>,
	method: PaymentMethodFeeSubjectModel,
	t: TranslateFn,
): string {
	const fee = formatPaymentMethodFeeLabel(types, method, t);

	return fee.length > 0 ? ` — ${fee}` : '';
}

/** The payment types a customer can add from a payment screen. */
const ADDABLE_PAYMENT_TYPES: ReadonlyArray<PaymentMethodTypeEnum> = [
	PaymentMethodTypeEnum.CREDIT_CARD,
	PaymentMethodTypeEnum.ACH,
];

/**
 * Which type the "Add payment method" form should open on from a payment
 * screen that no longer asks for a type first.
 *
 * The type of the method currently selected when the surface offers it, else
 * a card when cards are offered (the form's own default and the common case),
 * else the first addable type offered. Undefined when nothing addable is
 * offered, leaving the form on its default. It must land on an OFFERED type:
 * an account restricted to ACH would otherwise open on card fields it cannot
 * save.
 */
export function resolveAddPaymentMethodType(
	selectedMethodType: string | null | undefined,
	types: Array<Pick<SupportedPaymentMethodType, 'paymentMethodType'>>,
): PaymentMethodTypeEnum | undefined {
	const offered = ADDABLE_PAYMENT_TYPES.filter((addable) =>
		types.some((type) => type.paymentMethodType === (addable as string)),
	);

	return (
		offered.find((addable) => (addable as string) === selectedMethodType) ??
		offered.at(0)
	);
}
